import TableHeader from "src/components/TableHeader";
import {
  OFFER_DETAILS_EMAIL_CREATIVE_TITLES,
  OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS,
  OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_SORT,
} from "src/helpers/Constants";
import { OfferDetailsTableSortType, OperationType } from "src/helpers/Enums";

const formatRevenue = (amount) => {
  if (amount == null || isNaN(amount)) {
    return "-";
  }
  let formatted = Number(amount).toFixed(2);
  formatted = formatted.replace(/\.00$/, "");
  return `$${formatted}`;
};

const formatScore = (score) => {
  if (score == null || isNaN(score)) {
    return "-";
  }
  const numValue = parseFloat(score);
  if (isNaN(numValue)) {
    return score;
  }

  // If it's a whole number, return as is
  if (Number.isInteger(numValue)) {
    return numValue.toString();
  }

  // If decimal, show 2 decimal places and replace dot with comma
  return numValue.toFixed(2).replace(".", ",");
};

const getPendingSuggestion = (suggestions, creativeId) => {
  if (!suggestions?.length || !creativeId) return null;
  return (
    suggestions.find(
      (s) => s.creative_id === creativeId && s.status !== "resolved"
    ) || null
  );
};

const formatSuggestionType = (type) => {
  if (!type) return "";
  return String(type).replace(/_/g, " ");
};

const EmailCreativeTable = ({
  sortedList,
  page,
  totalPages,
  onPageChange,
  setList,
  list,
  onOpenCreativeDelete,
  onOpenNewCreative,
  suggestions = [],
  onCreativeResolved = () => {},
  onCreativeNoted = () => {},
}) => {
  return (
    <div className={"w-full flex flex-col justify-between"}>
      <div className={"w-full flex flex-col gap-3 px-6"}>
        <TableHeader
          titles={OFFER_DETAILS_EMAIL_CREATIVE_TITLES}
          widths={OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS}
          sort={OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_SORT}
          sortType={OfferDetailsTableSortType}
          setList={setList}
          list={list}
          textAlignments={[null, null, null, null, null]}
          showIconForColumns={[null, null, null, true, null]}
        />
        <div className="TableWrapper border-0">
          <table className="Table w-full">
            <tbody className="flex flex-col gap-2">
              {!sortedList || sortedList.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No Email creatives found
                  </td>
                </tr>
              ) : (
                sortedList.map((item, index) => {
                  const pendingSuggestion = getPendingSuggestion(
                    suggestions,
                    item?._id
                  );
                  return (
                    <tr
                      key={index}
                      className={
                        "flex hover:bg-[#F6F6F9] rounded-lg border border-[#DBDFE8] text-[#15151F] text-[11px] font-medium leading-[14px] w-full"
                      }
                      style={{
                        fontFamily: "Inter",
                      }}
                    >
                      <td
                        className="py-3 px-[10px] border-r flex-shrink-0"
                        style={{
                          width:
                            OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS[0],
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-3">
                              {pendingSuggestion && (
                                <div
                                  className="w-[3px] h-8 rounded-full flex-shrink-0"
                                  style={{
                                    backgroundColor:
                                      pendingSuggestion.status === "pending"
                                        ? "#DD2E2E"
                                        : "#F0970B",
                                  }}
                                />
                              )}
                              <span>
                                {item?.name || item?.offer?.name || "N/A"}
                              </span>
                            </div>

                            {pendingSuggestion && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                                  pendingSuggestion.status === "pending"
                                    ? "bg-[#FFD2D2] text-[#810D0D]"
                                    : "bg-[#FFF4D3] text-[#7D510E]"
                                }`}
                                style={{ fontFamily: "Inter" }}
                              >
                                {formatSuggestionType(pendingSuggestion.type)}
                              </span>
                            )}

                            {pendingSuggestion && (
                              <div className="flex items-center gap-[4px] shrink-0 w-fit">
                                <button
                                  title="Resolve the suggestions raised for this creative"
                                  onClick={() => onCreativeResolved(item)}
                                  className="flex h-[22px] px-2 shrink-0 w-fit whitespace-nowrap justify-center items-center gap-1 rounded-md border border-[#E5E7F0] bg-white"
                                >
                                  <span
                                    className="text-[#15151F] text-[11px] font-semibold leading-normal"
                                    style={{ fontFamily: "Inter" }}
                                  >
                                    Resolved
                                  </span>
                                </button>
                                {pendingSuggestion.status === "pending" && (
                                  <button
                                    title="Note the suggestions raised for this creative"
                                    onClick={() => onCreativeNoted(item)}
                                    className="flex h-[22px] px-2 shrink-0 w-fit whitespace-nowrap justify-center items-center gap-1 rounded-md border border-[#E5E7F0] bg-white"
                                  >
                                    <span
                                      className="text-[#15151F] text-[11px] font-semibold leading-normal"
                                      style={{ fontFamily: "Inter" }}
                                    >
                                      Noted
                                    </span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div
                            className="text-[#8185A8] text-[11px] font-normal leading-normal"
                            style={{ fontFamily: "Inter" }}
                          >
                            {item.sentCount
                              ? `Sent ${item.sentCount} times`
                              : "-"}
                          </div>
                        </div>
                      </td>
                      <td
                        className="py-3 px-5 border-r flex-shrink-0 flex items-center"
                        style={{
                          width:
                            OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS[1],
                        }}
                      >
                        {item?.message || item?.note || "-"}
                      </td>
                      <td
                        className="py-3 px-5 border-r flex-shrink-0 flex items-center"
                        style={{
                          width:
                            OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS[2],
                        }}
                      >
                        {item?.score != null ? formatScore(item.score) : "-"}
                      </td>
                      <td
                        className="py-3 px-5 border-r flex-shrink-0 flex items-center"
                        style={{
                          width:
                            OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS[3],
                        }}
                      >
                        {item?.revenue || item?.totalRevenue ? (
                          <div
                            className="inline-flex items-center h-[18px] px-[6px] rounded-[34px] bg-[#E5E7F0] text-[#15151F] text-[11px] font-semibold"
                            style={{ fontFamily: "Inter" }}
                          >
                            {formatRevenue(item.revenue || item.totalRevenue)}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td
                        className="py-2 px-[6px] flex-shrink-0 flex items-center justify-center"
                        style={{
                          width:
                            OFFER_DETAILS_EMAIL_CREATIVE_TABLE_TITLE_WIDTHS[4],
                        }}
                      >
                        <div className="flex">
                          <button
                            className="OfferDetailsEditButton"
                            onClick={() =>
                              onOpenNewCreative(OperationType.UPDATE, item)
                            }
                          >
                            <img
                              alt={"Logo"}
                              src={"/media/icons/editIcon.svg"}
                              className={
                                "w-[14px] h-[14px] min-w-[14px] min-h-[14px]"
                              }
                            />
                          </button>
                          <button
                            className="OfferDetailsDeleteButton"
                            onClick={() => onOpenCreativeDelete(item?._id)}
                          >
                            <img
                              alt={"Logo"}
                              src={"/media/icons/deleteIcon.svg"}
                              className={
                                "w-[14px] h-[14px] min-w-[14px] min-h-[14px]"
                              }
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmailCreativeTable;
